import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/dealership/lib/api";
import { loadDealer } from "@/dealership/lib/dealer";
import { BillOfSaleDocument, SAMPLE_DEAL } from "@/dealership/components/BillOfSaleDocument";
import { DEFAULT_TEXTS, TEXT_GROUPS, TOKENS, mergeTexts, textOverrides } from "@/dealership/lib/billOfSaleText";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft, Save, RotateCcw, Plus, X, PenLine, Type, Trash2, UserPlus, MousePointer2, FileSignature, AlignLeft,
} from "lucide-react";

const PALETTE = ["#2563eb", "#7c3aed", "#059669", "#dc2626", "#d97706", "#0891b2", "#db2777", "#4f46e5"];
const uid = (p) => `${p}_${Date.now()}_${Math.floor(Math.random() * 1000)}`;

/** Draggable marker layer shown over one page of the mock Bill of Sale. */
function EditorOverlay({ pageNum, spots, signers, selectedId, onSelect, onMove, onUpdate, onDelete }) {
  const layerRef = useRef(null);
  const pageSpots = spots.filter((s) => s.page === pageNum);
  const signerOf = (id) => signers.find((s) => s.id === id);

  const startDrag = (id, e) => {
    e.stopPropagation();
    onSelect(id);
    const onMoveEvt = (ev) => {
      if (!layerRef.current) return;
      const r = layerRef.current.getBoundingClientRect();
      const x = Math.min(1, Math.max(0, (ev.clientX - r.left) / r.width));
      const y = Math.min(1, Math.max(0, (ev.clientY - r.top) / r.height));
      onMove(id, x, y);
    };
    const onUpEvt = () => {
      window.removeEventListener("pointermove", onMoveEvt);
      window.removeEventListener("pointerup", onUpEvt);
    };
    window.addEventListener("pointermove", onMoveEvt);
    window.addEventListener("pointerup", onUpEvt);
  };

  return (
    <div
      ref={layerRef}
      className="absolute inset-0 pointer-events-auto"
      onPointerDown={() => onSelect(null)}
      data-testid={`editor-overlay-${pageNum}`}
    >
      {pageSpots.map((s) => {
        const signer = signerOf(s.signer_id);
        const color = signer?.color || "#334155";
        const selected = selectedId === s.id;
        const isInitial = s.type === "initial";
        return (
          <div
            key={s.id}
            className={`absolute select-none touch-none ${selected ? "z-40" : "z-30"}`}
            style={{ left: `${s.x * 100}%`, top: `${s.y * 100}%`, transform: "translate(-50%, -50%)" }}
          >
            <button
              onPointerDown={(e) => startDrag(s.id, e)}
              className="cursor-move rounded-lg border-2 px-2 py-1 flex items-center gap-1 text-[10px] font-semibold shadow-sm whitespace-nowrap touch-none"
              style={{ borderColor: color, color, background: `${color}1a`, outline: selected ? `2px solid ${color}` : "none", outlineOffset: 2 }}
              data-testid={`editor-spot-${s.id}`}
              title="Drag to position"
            >
              {isInitial ? <Type className="h-3 w-3" /> : <PenLine className="h-3 w-3" />}
              {signer?.label || "?"}
            </button>

            {selected && (
              <div
                className="absolute left-1/2 -translate-x-1/2 -top-2 -translate-y-full bg-white border border-zinc-200 rounded-xl shadow-lg p-1.5 flex items-center gap-1"
                onPointerDown={(e) => e.stopPropagation()}
                data-testid={`editor-spot-controls-${s.id}`}
              >
                <div className="flex rounded-lg overflow-hidden border border-zinc-200">
                  <button onClick={() => onUpdate(s.id, { type: "signature" })} className={`px-2 py-1 text-[10px] flex items-center gap-1 ${!isInitial ? "bg-zinc-900 text-white" : "text-zinc-600"}`} data-testid={`editor-spot-type-sig-${s.id}`}><PenLine className="h-3 w-3" />Sig</button>
                  <button onClick={() => onUpdate(s.id, { type: "initial" })} className={`px-2 py-1 text-[10px] flex items-center gap-1 ${isInitial ? "bg-zinc-900 text-white" : "text-zinc-600"}`} data-testid={`editor-spot-type-init-${s.id}`}><Type className="h-3 w-3" />Init</button>
                </div>
                <select
                  value={s.signer_id}
                  onChange={(e) => onUpdate(s.id, { signer_id: e.target.value })}
                  className="h-7 text-[10px] rounded-lg border border-zinc-200 bg-white px-1 max-w-[9rem] text-zinc-800"
                  data-testid={`editor-spot-signer-${s.id}`}
                >
                  {signers.map((sg) => <option key={sg.id} value={sg.id}>{sg.label}</option>)}
                </select>
                <button onClick={() => onDelete(s.id)} className="p-1 rounded-lg text-zinc-500 hover:text-red-500 hover:bg-red-50" title="Delete spot" data-testid={`editor-spot-delete-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function DealerSettings() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const dealer = loadDealer();
  const { data: loaded } = useQuery({ queryKey: ["bos-settings"], queryFn: async () => (await api.get("/bill-of-sale/settings")).data });

  const [signers, setSigners] = useState(null);
  const [spots, setSpots] = useState([]);
  const [texts, setTexts] = useState(null);
  const [tab, setTab] = useState("spots");
  const [page, setPage] = useState(1);
  const [activeSigner, setActiveSigner] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (loaded && signers == null) {
      setSigners(loaded.signers || []);
      setSpots(loaded.spots || []);
      setTexts(mergeTexts(loaded.texts));
      setActiveSigner((loaded.signers || [])[0]?.id || null);
    }
  }, [loaded, signers]);

  if (signers == null || texts == null) return <div className="p-8 text-muted-foreground">Loading Bill of Sale settings…</div>;

  const spotCount = spots.length;

  const addSpot = (type) => {
    if (!activeSigner) { toast.error("Add a signer first"); return; }
    const id = uid("spot");
    setSpots((cur) => [...cur, { id, page, x: 0.5, y: 0.5, type, signer_id: activeSigner, label: type === "initial" ? "Initials" : "Signature" }]);
    setSelectedId(id);
  };
  const moveSpot = (id, x, y) => setSpots((cur) => cur.map((s) => (s.id === id ? { ...s, x, y } : s)));
  const updateSpot = (id, patch) => setSpots((cur) => cur.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  const deleteSpot = (id) => { setSpots((cur) => cur.filter((s) => s.id !== id)); setSelectedId(null); };

  const setText = (key, val) => setTexts((cur) => ({ ...cur, [key]: val }));
  const resetText = (key) => setTexts((cur) => ({ ...cur, [key]: DEFAULT_TEXTS[key] }));
  const resetAllText = () => {
    if (!window.confirm("Restore ALL document wording to the default legal text? Your edits will be replaced.")) return;
    setTexts({ ...DEFAULT_TEXTS });
  };
  const editedCount = Object.keys(textOverrides(texts)).length;

  const addSigner = () => {
    const id = uid("signer");
    const color = PALETTE[signers.length % PALETTE.length];
    setSigners((cur) => [...cur, { id, label: "New Signer", color }]);
    setActiveSigner(id);
  };
  const updateSigner = (id, patch) => setSigners((cur) => cur.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  const deleteSigner = (id) => {
    const used = spots.filter((s) => s.signer_id === id).length;
    if (used > 0 && !window.confirm(`This signer has ${used} placed spot(s). Delete the signer and its spots?`)) return;
    setSigners((cur) => cur.filter((s) => s.id !== id));
    setSpots((cur) => cur.filter((s) => s.signer_id !== id));
    if (activeSigner === id) setActiveSigner(signers.find((s) => s.id !== id)?.id || null);
  };

  const save = async () => {
    if (signers.some((s) => !s.label.trim())) { toast.error("Every signer needs a name"); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/bill-of-sale/settings", { signers, spots, texts: textOverrides(texts) });
      setSigners(data.signers);
      setSpots(data.spots);
      setTexts(mergeTexts(data.texts));
      qc.setQueryData(["bos-settings"], data);
      toast.success("Bill of Sale settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Reset the signing layout (signers and spots) back to the defaults? Your document wording is kept.")) return;
    const { data } = await api.post("/bill-of-sale/settings/reset");
    setSigners(data.signers);
    setSpots(data.spots);
    setTexts(mergeTexts(data.texts));
    setActiveSigner(data.signers[0]?.id || null);
    setSelectedId(null);
    qc.setQueryData(["bos-settings"], data);
    toast.success("Default layout restored");
  };

  const pageSpotCount = (n) => spots.filter((s) => s.page === n).length;

  return (
    <div className="p-6 sm:p-8 pb-24 max-w-6xl" data-testid="dealer-settings-page">
      <button onClick={() => navigate("/dealership/deals")} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5 mb-3" data-testid="settings-back">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to deals
      </button>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl flex items-center gap-3"><FileSignature className="h-8 w-8 text-primary" />Bill of Sale</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            {tab === "spots"
              ? <>Place where each signer must <b>sign</b> or <b>initial</b>. Drag a marker onto the mock document. Every deal's Bill of Sale shows these exact fillable spots.</>
              : <>Edit the wording that prints on every Bill of Sale — headings, disclosures and the terms & conditions. Deal details (name, vehicle, prices) fill in automatically.</>}
          </p>
        </div>
        <div className="flex gap-2">
          {tab === "spots"
            ? <Button variant="ghost" onClick={reset} data-testid="settings-reset"><RotateCcw className="h-4 w-4 mr-2" />Reset layout</Button>
            : <Button variant="ghost" onClick={resetAllText} data-testid="text-reset-all"><RotateCcw className="h-4 w-4 mr-2" />Reset all text</Button>}
          <Button onClick={save} disabled={saving} data-testid="settings-save"><Save className="h-4 w-4 mr-2" />{saving ? "Saving…" : "Save"}</Button>
        </div>
      </div>

      {/* Tab switch */}
      <div className="inline-flex rounded-xl border border-border p-1 bg-card/40 mb-5" data-testid="settings-tabs">
        <button onClick={() => setTab("spots")} data-testid="tab-spots"
          className={`px-4 py-1.5 rounded-lg text-sm flex items-center gap-2 transition-colors ${tab === "spots" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
          <FileSignature className="h-4 w-4" />Signing spots
        </button>
        <button onClick={() => { setTab("text"); setSelectedId(null); }} data-testid="tab-text"
          className={`px-4 py-1.5 rounded-lg text-sm flex items-center gap-2 transition-colors ${tab === "text" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
          <AlignLeft className="h-4 w-4" />Document text{editedCount > 0 && <span className="ml-1 bg-emerald-500 text-white text-[10px] rounded-full px-1.5 py-0.5 font-bold">{editedCount}</span>}
        </button>
      </div>

      {tab === "spots" && (
        <>
          {/* Signers */}
          <div className="rounded-2xl border border-border bg-card/40 p-4 mb-4" data-testid="signers-panel">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading text-lg">Signers</h2>
              <Button size="sm" variant="outline" onClick={addSigner} data-testid="add-signer"><UserPlus className="h-3.5 w-3.5 mr-1.5" />Add signer</Button>
            </div>
            <div className="space-y-2">
              {signers.map((sg) => (
                <div key={sg.id} className="flex items-center gap-2" data-testid={`signer-row-${sg.id}`}>
                  <input
                    type="color" value={sg.color} onChange={(e) => updateSigner(sg.id, { color: e.target.value })}
                    className="h-8 w-8 rounded-lg border border-border bg-transparent cursor-pointer shrink-0" title="Marker colour"
                    data-testid={`signer-color-${sg.id}`}
                  />
                  <Input value={sg.label} onChange={(e) => updateSigner(sg.id, { label: e.target.value })} className="h-9 flex-1 max-w-sm" placeholder="Signer name" data-testid={`signer-label-${sg.id}`} />
                  <span className="text-xs text-muted-foreground tnum">{spots.filter((s) => s.signer_id === sg.id).length} spot(s)</span>
                  <button onClick={() => deleteSigner(sg.id)} className="ml-auto p-2 rounded-lg text-muted-foreground hover:text-red-400 hover:bg-red-500/10" title="Delete signer" data-testid={`signer-delete-${sg.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              {signers.length === 0 && <p className="text-sm text-muted-foreground">No signers yet — add one to start placing spots.</p>}
            </div>
          </div>

          {/* Toolbar */}
          <div className="rounded-2xl border border-border bg-card/40 p-4 mb-4 flex flex-wrap items-center gap-x-6 gap-y-3" data-testid="editor-toolbar">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="label-mono">For</span>
              {signers.map((sg) => (
                <button key={sg.id} onClick={() => setActiveSigner(sg.id)} data-testid={`active-signer-${sg.id}`}
                  className={`px-3 py-1.5 rounded-full border text-xs flex items-center gap-1.5 transition-colors ${activeSigner === sg.id ? "border-transparent text-white" : "border-border text-muted-foreground hover:border-primary/40"}`}
                  style={activeSigner === sg.id ? { background: sg.color } : {}}>
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: sg.color }} />{sg.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <Button size="sm" variant="outline" onClick={() => addSpot("signature")} data-testid="add-signature-spot"><PenLine className="h-3.5 w-3.5 mr-1.5" />Add signature</Button>
              <Button size="sm" variant="outline" onClick={() => addSpot("initial")} data-testid="add-initial-spot"><Type className="h-3.5 w-3.5 mr-1.5" />Add initials</Button>
            </div>
          </div>

          <p className="text-xs text-muted-foreground mb-3 flex items-center gap-1.5"><MousePointer2 className="h-3.5 w-3.5" />Drag markers to reposition · click a marker to change its signer/type or delete it · adds to the page shown below · {spotCount} spot(s) total</p>
        </>
      )}

      {tab === "text" && (
        <div className="rounded-2xl border border-border bg-card/40 p-4 mb-4 space-y-2" data-testid="text-editor">
          <p className="text-xs text-muted-foreground mb-1">Tokens auto-fill from the deal: {TOKENS.join("  ")}</p>
          {TEXT_GROUPS.map((grp, gi) => (
            <details key={grp.group} open={gi === 0} className="rounded-xl border border-border/60 bg-background/40">
              <summary className="cursor-pointer select-none px-4 py-2.5 font-heading text-sm">{grp.group}</summary>
              <div className="px-4 pb-4 pt-1 space-y-3">
                {grp.fields.map((f) => {
                  const changed = texts[f.key] !== DEFAULT_TEXTS[f.key];
                  return (
                    <div key={f.key}>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs text-muted-foreground">{f.label}{f.tokens && <span className="ml-1 text-[10px] text-primary">· tokens ok</span>}</label>
                        {changed && <button onClick={() => resetText(f.key)} className="text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid={`text-reset-${f.key}`}><RotateCcw className="h-3 w-3" />Reset</button>}
                      </div>
                      {f.rows > 1
                        ? <textarea value={texts[f.key]} onChange={(e) => setText(f.key, e.target.value)} rows={f.rows}
                            className={`w-full rounded-lg border bg-input px-3 py-2 text-sm resize-y focus:outline-none focus:ring-1 focus:ring-primary ${changed ? "border-primary/50" : "border-border"}`}
                            data-testid={`text-field-${f.key}`} />
                        : <Input value={texts[f.key]} onChange={(e) => setText(f.key, e.target.value)} className={`h-9 ${changed ? "border-primary/50" : ""}`} data-testid={`text-field-${f.key}`} />}
                    </div>
                  );
                })}
              </div>
            </details>
          ))}
        </div>
      )}

      {/* Shared preview */}
      <div className="flex items-center gap-2 mb-3" data-testid="preview-pages">
        <span className="label-mono">Preview page</span>
        {[1, 2, 3, 4].map((n) => (
          <button key={n} onClick={() => { setPage(n); setSelectedId(null); }} data-testid={`editor-page-${n}`}
            className={`w-9 h-9 rounded-lg border text-sm tnum relative transition-colors ${page === n ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/40"}`}>
            {n}
            {tab === "spots" && pageSpotCount(n) > 0 && <span className="absolute -top-1.5 -right-1.5 bg-emerald-500 text-white text-[9px] rounded-full min-w-[15px] h-[15px] px-1 flex items-center justify-center font-bold">{pageSpotCount(n)}</span>}
          </button>
        ))}
      </div>

      <div className="rounded-2xl border border-border bg-zinc-200 p-4 overflow-x-auto" data-testid="mock-bill-of-sale">
        <BillOfSaleDocument
          d={SAMPLE_DEAL}
          dealer={dealer}
          texts={texts}
          only={page}
          overlay={tab === "spots" ? () => (
            <EditorOverlay
              pageNum={page}
              spots={spots}
              signers={signers}
              selectedId={selectedId}
              onSelect={setSelectedId}
              onMove={moveSpot}
              onUpdate={updateSpot}
              onDelete={deleteSpot}
            />
          ) : undefined}
        />
      </div>

      <div className="flex justify-end gap-2 mt-6">
        <Button variant="ghost" onClick={() => navigate("/dealership/deals")}>Cancel</Button>
        <Button onClick={save} disabled={saving} data-testid="settings-save-bottom"><Save className="h-4 w-4 mr-2" />{saving ? "Saving…" : "Save"}</Button>
      </div>
    </div>
  );
}
